import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [authOpen, setAuthOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [visualOpen, setVisualOpen] = useState(false);

  const loadCart = useCallback(async () => {
    const token = localStorage.getItem("nova_token");
    if (!token) return;
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("nova_token");
    if (!token) return;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      await loadCart();
    } catch (e) {
      localStorage.removeItem("nova_token");
    }
  }, [loadCart]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("nova_token", data.token);
    setUser(data.user);
    await loadCart();
    toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("nova_token", data.token);
    setUser(data.user);
    await loadCart();
    toast.success(`Welcome to Nova Market, ${name.split(" ")[0]}`);
  };

  const logout = () => {
    localStorage.removeItem("nova_token");
    setUser(null);
    setCart({ items: [], total: 0 });
    toast("Signed out");
  };

  const addToCart = async (product_id, quantity = 1) => {
    if (!user) { setAuthOpen(true); toast.error("Sign in to save items"); return; }
    await api.post("/cart/add", { product_id, quantity });
    await loadCart();
    toast.success("Added to cart");
  };

  const updateCart = async (product_id, quantity) => {
    await api.post("/cart/update", { product_id, quantity });
    await loadCart();
  };

  return (
    <AppContext.Provider value={{
      user, setUser, cart, loadCart, login, register, logout, addToCart, updateCart,
      authOpen, setAuthOpen, assistantOpen, setAssistantOpen, visualOpen, setVisualOpen,
    }}>
      {children}
    </AppContext.Provider>
  );
}
